'use client'

// Anchor that re-writes its href on click to include the visitor's stored
// first-touch UTM/click-ID attribution. Use for outbound links to
// virtualracingschool.appspot.com (the Java members app), so that app's
// analytics can attribute academy subscription starts back to the Meta ad the
// user originally clicked.
//
// Keeps the visible href stable (avoids odd hover-preview URLs) and only
// swaps it in the onClick handler right before navigation, so:
//  - Attribution is CURRENT at click time (any late localStorage write from
//    late `useEffect` capture has landed by then).
//  - Right-click / open-in-new-tab / middle-click still get a working URL
//    (no UTMs, but the link still works — attribution is best-effort not
//    required).
//
// Safe on SSR: `readAttribution` is guarded for `window === undefined`.

import React, { useCallback } from 'react'
import { appendAttributionToUrl } from '../utils/attribution'

export default function AttributionLink({ href, children, ...rest }) {
  const onClick = useCallback((e) => {
    if (rest.onClick) rest.onClick(e)
    if (e.defaultPrevented) return
    // Left-click without modifier keys only — respect ctrl/cmd/shift/middle
    // click (open in new tab / new window), which fire without the rewritten
    // href but still work fine.
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return
    const tagged = appendAttributionToUrl(href)
    if (!tagged || tagged === href) return
    e.preventDefault()
    // Preserve target=_blank if the caller set it, otherwise same-tab.
    if (rest.target === '_blank') {
      window.open(tagged, '_blank', rest.rel || 'noopener noreferrer')
    } else {
      window.location.href = tagged
    }
  }, [href, rest])

  return (
    <a href={href} onClick={onClick} {...rest}>
      {children}
    </a>
  )
}
